/**
 * Same-origin reachability check for an opaque coo-api failure (TEAM-15511).
 *
 * The browser calls `prod-coo-api.chowly.io` cross-origin, and a failed call rejects with a bare
 * `TypeError` that says nothing about where it failed. Asking the storefront's own origin whether
 * it still answers splits that bucket: if the origin answers, the diner's connection works and
 * only coo-api is unreachable from their network (a network that blocks or breaks that hostname,
 * or a CORS-less edge error during an incident); if neither answers, the diner has no connection.
 *
 * The probe is `HEAD /ping`: the Next health route (`pages/api/ping.ts`, rewritten in
 * `next.config.mjs`), served by the same app on every brand domain and cheap to answer.
 * - `HEAD` stays out of the Caddy edge cache (a HEAD response carries no `x-cache-status`).
 * - `cache: 'no-store'` plus a timestamp query keep browser and proxy caches out of it; the
 *   edge cache keys on host + path only, so the query alone would not bypass that one.
 * - Any response counts as reachable, whatever its status: a non-US diner on a custom domain gets
 *   the geo-gate 403, which still proves the origin answered.
 * - `redirect: 'manual'` keeps the check on this origin; an opaque redirect is still an answer.
 *
 * It never throws and is bounded by its own timer, so a slow origin cannot hold the capture
 * forever.
 */

export const SAME_ORIGIN_PROBE_RESULTS = ['ok', 'failed', 'timeout', 'skipped'] as const;

export type SameOriginProbeResult = (typeof SAME_ORIGIN_PROBE_RESULTS)[number];

/** How long the probe waits for the origin before calling the check inconclusive. */
export const SAME_ORIGIN_PROBE_TIMEOUT_MS = 2000;

/** The storefront's own health route (rewritten to `/api/ping`). */
export const SAME_ORIGIN_PROBE_PATH = '/ping';

/**
 * Ask the storefront's own origin whether it answers.
 *
 * - `ok` — any HTTP response came back.
 * - `failed` — the request rejected (no connection to the origin either).
 * - `timeout` — no answer within `timeoutMs`; inconclusive.
 * - `skipped` — no browser `fetch`/`AbortController` to probe with (SSR, tests).
 */
export async function probeSameOrigin(
  timeoutMs: number = SAME_ORIGIN_PROBE_TIMEOUT_MS,
): Promise<SameOriginProbeResult> {
  if (
    typeof window === 'undefined' ||
    typeof window.fetch !== 'function' ||
    typeof AbortController === 'undefined'
  ) {
    return 'skipped';
  }

  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    await window.fetch(`${window.location.origin}${SAME_ORIGIN_PROBE_PATH}?_=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'manual',
      signal: controller.signal,
    });
    return 'ok';
  } catch {
    return timedOut ? 'timeout' : 'failed';
  } finally {
    clearTimeout(timer);
  }
}
